import { ApolloClient } from "@apollo/client";
import produce from "immer";
import createHook from "zustand";
import create from "zustand/vanilla";

import { ReportAnalyticsRbacPermissions } from "../../pages/Report/store/enums";

// schema
import { FieldFilter, FieldValue } from "../../schemas/report";

// API queries
import {
	GET_CUSTOM_FIELDS,
	GET_EMPLOYEES,
	GET_GROUPS,
	GET_REVIEW_CYCLES,
	GET_REVIEW_TEMPLATES,
	GET_SELECT_FIELD_OPTIONS,
} from "../../hooks/useQuery";

import { getOutlooks, getSearch, getStrategyCategories } from "../../../../api/services";
import { getRbacPrimaryRolePermissions } from "../../../../api/services/defs/rbac";

import { getColor } from "../../../../utils/getColor";

export interface SelectInputValue<T> {
	value: T;
	label: string;
}

export interface QueryBuilderStore {
	/**
	 * Apollo client:
	 */
	client: ApolloClient<any> | null;
	setClient: (client: any) => void;
	/**
	 * Filters:
	 * stagedFilters are any complete filters that have been added
	 * filterUpdate includes any in-progress updates to the saved filters
	 */
	// any changes to the filters are stored here until saved
	filterUpdate: FieldFilter[][];
	setFilterUpdate: (filterUpdate: FieldFilter[][]) => void;
	setFilterUpdateGroup: (filterIndex: number, filterUpdate: FieldFilter[]) => void;
	// check for changes to a filter group
	filterUpdateHasChanges: (savedFilters?: FieldFilter[][] | null) => boolean;
	/**
	 * Selected filter state management:
	 */
	selectedFilterGroup: number | null;
	setSelectedFilterGroup: (selectedFilterGroup: number | null) => void;
	/**
	 * Handler methods for updating the filter update:
	 */
	handleSelectFilterGroup: (filterGroupIndex: number) => void;
	handleRemoveFilterGroup: (filterGroupIndex: number) => void;
	handleRemoveFilter: (filterGroupIndex: number, filterIndex: number) => void;
	/**
	 * Value Label Maps:
	 * valueLabelMaps provide a way of accessing the labels
	 * for values selected for a filter, to be used in
	 * displaying the visual representation of the filter
	 */
	valueLabelMaps: {
		customFields: { [key: string]: string };
		groups: { [key: string]: string };
		people: { [key: string]: string };
		reviewCycles: { [key: string]: string };
		reviewForms: { [key: string]: string };
		reviewTemplates: { [key: string]: string };
		goal: { [key: string]: string };
		goalCategories: { [key: string]: string };
		goalOutlook: { [key: string]: { color: string; label: string; id: string } };
	};
	initValueLabelMaps: () => void;
	setValueLabelMap: (key: string, valueMap: { [key: string]: string }) => void;
	/**
	 * API calls with label mapping:
	 *
	 * Whenever any of these is made, either from selecting a filter option
	 * that requires a lookup, or from reading the values needed for displaying a filter,
	 * the corresponding valueLabelMap should be updated for reference later:
	 * {value: label} as { [key: string]: string }
	 * and the results of the API call returned for use in SelectInput
	 * [{value: string, label: string}]
	 */
	fetchEmployees: (searchTerm: string) => Promise<SelectInputValue<string>[]>;
	// fetchSpecificEmployees - used to lookup names of any 'people' type ids found in filters
	fetchSpecificEmployees: (ids: string[]) => Promise<void>; // does not return anything
	fetchReviewTemplates: () => Promise<SelectInputValue<string>[]>;
	fetchReviewCycles: () => Promise<SelectInputValue<string>[]>;
	fetchCustomFields: () => Promise<SelectInputValue<string>[]>;
	fetchGroups: () => Promise<SelectInputValue<string>[]>;
	fetchGoals: (searchTerm: string | null, _ids?: string[]) => Promise<SelectInputValue<string>[]>;
	fetchGoalCategories: () => Promise<SelectInputValue<string>[]>;
	fetchGoalOutlooks: () => Promise<SelectInputValue<string>[]>;
	/**
	 * Pure API calls:
	 * These just return the results for a SelectInput as SelectInputValue<string>[]:
	 */
	fetchSelectFieldOptions: (customFieldName: string) => Promise<SelectInputValue<string>[]>;
}

export const store = create<QueryBuilderStore>((set, get) => {
	const getClient = () => {
		const client = get().client;
		if (!client) throw new Error("Apollo client not set");
		return client;
	};

	return {
		/**
		 * Apollo client:
		 */
		client: null,
		setClient: (client) => set({ client }),
		/**
		 * Filters:
		 */
		filterUpdate: [],
		setFilterUpdate: (filterUpdate: FieldFilter[][]) => {
			set(
				produce((draft) => {
					draft.filterUpdate = filterUpdate;
				})
			);
		},
		setFilterUpdateGroup: (filterIndex: number | null, updatedFilterGroup: FieldFilter[]) => {
			set(
				produce((draft) => {
					if (filterIndex === null) {
						draft.filterUpdate.push(updatedFilterGroup);
					} else {
						draft.filterUpdate[filterIndex] = updatedFilterGroup;
					}
				})
			);
		},
		filterUpdateHasChanges: (savedFilters) => {
			const filterUpdate = get().filterUpdate;
			return JSON.stringify(filterUpdate) !== JSON.stringify(savedFilters);
		},
		/**
		 * Selected filter state management:
		 */
		selectedFilterGroup: null,
		setSelectedFilterGroup: (selectedFilterGroup: number | null) => set({ selectedFilterGroup }),
		handleSelectFilterGroup: (filterGroupIndex) => {
			get().setSelectedFilterGroup(filterGroupIndex);
		},
		handleRemoveFilterGroup: (filterGroupIndex) => {
			// firstly remove the item from the filter group
			set(
				produce((draft) => {
					draft.filterUpdate.splice(filterGroupIndex, 1);
				})
			);
		},
		handleRemoveFilter: (filterGroupIndex, filterItemIndex) => {
			// firstly remove the item from the filter group
			set(
				produce((draft) => {
					draft.filterUpdate[filterGroupIndex].splice(filterItemIndex, 1);
				})
			);
			// handle clearing empty groups and selecting the next filter
			set(
				produce((draft) => {
					// if the last filter is removed, remove the whole group
					draft.filterUpdate = draft.filterUpdate.filter((filterGroup) => filterGroup.length);
					// if there are no filters left in the group, select none
					if (draft.filterUpdate[draft.selectedFilterGroup]?.length === 0) {
						draft.selectedFilterGroup = null;
					}
				})
			);
		},
		/**
		 * Value Label Maps:
		 */
		valueLabelMaps: {
			customFields: {},
			groups: {},
			people: {},
			reviewCycles: {},
			reviewForms: {},
			reviewTemplates: {},
			goal: {},
			goalCategories: {},
			goalOutlook: {},
		},
		initValueLabelMaps: () => {
			// Reads through all filters and make appropriate API calls to populate valueLabelMaps
			// for use in displaying selected filters
			const {
				filterUpdate,
				fetchGroups,
				fetchSpecificEmployees,
				fetchReviewCycles,
				fetchReviewTemplates,
				fetchGoalCategories,
				fetchGoalOutlooks,
				fetchGoals,
			} = get();
			if (filterUpdate.length) {
				const employeesToFetch: FieldValue[] = [];

				filterUpdate.forEach((filterGroup) => {
					if (["person", "manager", "people"].includes(filterGroup[0].type)) {
						filterGroup.forEach((filter) => {
							if (["person", "manager"].includes(filter.type)) {
								// Person and manager type filters are single ids
								employeesToFetch.push(filter.value as FieldValue);
							} else if (filter.type === "people") {
								// People type filters are arrays of ids
								(filter.value as FieldValue[]).forEach((employee) => employeesToFetch.push(employee));
							}
						});

						if (employeesToFetch.length) {
							// I really hate this casting, but the effort I'm working on is to make TS happy. I'm afraid that
							// a more solid solution would require a lot of refactoring. Will be worth the change if we're
							// in here again to manipulate logic in any way.
							fetchSpecificEmployees(employeesToFetch as string[]);
						}
					} else if (filterGroup[0].type === "cycle") {
						fetchReviewCycles();
					} else if (filterGroup[0].type === "department") {
						fetchGroups();
					} else if (filterGroup[0].type === "template") {
						fetchReviewTemplates();
					} else if (filterGroup[0].type === "goal") {
						fetchGoals(
							null,
							filterGroup.map((filter) => filter.value as string)
						);
					} else if (filterGroup[0].type === "goalCategories") {
						fetchGoalCategories();
					} else if (filterGroup[0].type === "goalOutlook") {
						fetchGoalOutlooks();
					}
				});
			}
		},
		setValueLabelMap: (key: string, valueLabelMap: { [key: string]: string }) => {
			set(
				produce((draft) => {
					draft.valueLabelMaps[key] = valueLabelMap;
				})
			);
		},
		/**
		 * API calls with label mapping:
		 */
		fetchEmployees: async (searchTerm: string) => {
			const formatData = (employeeData) =>
				employeeData.map((employee) => ({
					value: employee._id,
					label: employee.name,
				}));
			const filters: { comparison: string; type: string; value: string; field: string }[][] = [];
			if (searchTerm.length) {
				filters.push([
					{
						comparison: "contains",
						type: "text",
						value: searchTerm,
						field: "name",
					},
				]);
			}

			const client = getClient();

			const { data } = await client.query({
				query: GET_EMPLOYEES,
				variables: {
					columns: [],
					page: "1",
					pageSize: "50",
					filters,
					sorts: [],
				},
			});
			const user_permissions = await getRbacPrimaryRolePermissions("report_analytics");

			const employeeData = (() => {
				if (user_permissions?.includes(ReportAnalyticsRbacPermissions.CreateReport)) {
					return data.employees;
				}
				// non admins should never enter this function anyways - this is just extra data security
				return [];
			})();

			const employeesWithNames = employeeData.filter((employee) => employee.name);
			const employeesAsFields = formatData(employeesWithNames);
			// add to employee label value map any new people found each time searching
			const vlmPeople = get().valueLabelMaps.people;
			const newPeople = employeesAsFields.reduce((acc, employee) => {
				if (!vlmPeople[employee.value]) {
					acc[employee.value] = employee.label;
				}
				return acc;
			}, {});
			const people = { ...vlmPeople, ...newPeople };
			const currentMap = get().valueLabelMaps.people;
			get().setValueLabelMap("people", { ...currentMap, ...people });
			// return search results
			return employeesAsFields;
		},
		fetchSpecificEmployees: async (ids: string[]) => {
			// fetch any employees for a list of ids and set the valueLabelMap
			const filters = [
				[
					...ids.map((id) => ({
						comparison: "eq",
						type: "person",
						value: id,
						field: "_id",
					})),
				],
			];

			const client = getClient();

			const { data } = await client.query({
				query: GET_EMPLOYEES,
				variables: {
					columns: [],
					filters,
					sorts: [],
					page: "1",
					pageSize: "50",
				},
			});
			const people = data.employees.reduce((acc, employee) => {
				if (employee.name) {
					acc[employee._id] = employee.name;
				}
				return acc;
			}, {});

			const currentMap = get().valueLabelMaps.people;
			get().setValueLabelMap("people", { ...currentMap, ...people });
		},
		fetchReviewTemplates: async () => {
			const formatData = (templateData) =>
				templateData.map((template) => ({
					value: template._id,
					label: template.name,
				}));

			const client = getClient();

			const { data } = await client.query({
				query: GET_REVIEW_TEMPLATES,
				variables: {},
			});
			const formattedData = formatData(data.reviewTemplates);
			const reviewTemplates = formattedData.reduce((acc, field) => {
				acc[field.value] = field.label;
				return acc;
			}, {});
			// only gets all review templates currently, so no need to check for new ones or keep existing
			get().setValueLabelMap("reviewTemplates", reviewTemplates);
			return formattedData;
		},
		fetchReviewCycles: async () => {
			const formatData = (cycleData) =>
				cycleData.map((template) => ({
					value: template._id,
					label: template.name,
				}));

			const client = getClient();

			const { data } = await client.query({
				query: GET_REVIEW_CYCLES,
				variables: {},
			});
			const formattedData = formatData(data.uniqueReviewCycleNames);
			const customFields = formattedData.reduce((acc, field) => {
				acc[field.value] = field.label;
				return acc;
			}, {});
			// only gets all review cycles currently, so no need to check for new ones or keep existing
			get().setValueLabelMap("reviewCycles", customFields);
			return formattedData;
		},
		fetchCustomFields: async () => {
			const formatData = (fieldData) =>
				fieldData.map((field) => ({
					value: field.name,
					label: field.name,
					type: field.type,
				}));

			const client = getClient();

			const { data } = await client.query({
				query: GET_CUSTOM_FIELDS,
				variables: {},
			});
			const formattedData = formatData(data.fields);
			const customFields = formattedData.reduce((acc, field) => {
				acc[field.value] = field.label;
				return acc;
			}, {});
			// only gets all custom fields, so no need to check for new ones or keep existing
			get().setValueLabelMap("customFields", customFields);
			return formattedData;
		},
		fetchGroups: async () => {
			const formatData = (groupData) =>
				groupData.map((group) => ({
					value: group._id,
					label: group.name,
				}));

			const client = getClient();

			const { data } = await client.query({
				query: GET_GROUPS,
				variables: {},
			});
			const formattedData = formatData(data.groups);
			const groups = formattedData.reduce((acc, group) => {
				acc[group.value] = group.label;
				return acc;
			}, {});
			// only gets all groups, so no need to check for new ones or keep existing
			get().setValueLabelMap("groups", groups);
			return formattedData;
		},
		fetchGoals: async (searchTerm: string | null, _ids?: string[]) => {
			const formatData = (goalData) =>
				goalData.map((goal) => ({
					value: goal._id,
					label: goal.name,
				}));

			const searchTerms = searchTerm?.length ? { name_re: searchTerm } : {};
			const searchIds = _ids?.length ? { _id_in: _ids } : {};

			const searchOptions = searchTerm?.length ? searchTerms : searchIds;

			const goalData = await getSearch("goal", searchOptions, 0, 50);
			const formattedData = formatData(goalData.results);

			const valueLabelMappedGoals = get().valueLabelMaps.goal;
			const newGoals = formattedData.reduce((acc, goal) => {
				if (!valueLabelMappedGoals[goal.value]) {
					// add any new goals not already mapped
					acc[goal.value] = goal.label;
				}
				return acc;
			}, {});

			const goals = { ...valueLabelMappedGoals, ...newGoals };

			const currentMap = get().valueLabelMaps.goal;

			get().setValueLabelMap("goal", { ...currentMap, ...goals });

			return formattedData;
		},
		fetchGoalCategories: async () => {
			const formatData = (goalCategoryData) =>
				goalCategoryData.map((cat) => ({
					value: cat._id,
					label: cat.name,
				}));

			const { categories } = await getStrategyCategories();
			const formattedData = formatData(categories);

			const goalCategories = formattedData.reduce((acc, cat) => {
				acc[cat.value] = cat.label;
				return acc;
			}, {});
			// only gets all groups, so no need to check for new ones or keep existing
			get().setValueLabelMap("goalCategories", goalCategories);

			return formattedData;
		},
		fetchGoalOutlooks: async () => {
			const formatData = (goalOutlookData) =>
				goalOutlookData.map((outlook) => ({
					value: outlook.id,
					label: outlook.name,
					color: outlook.color || getColor("primary", 5),
				}));

			const { colors } = await getOutlooks();
			const formattedData = formatData(colors);
			const goalOutlooks = formattedData.reduce((acc, outlook) => {
				acc[outlook.value] = outlook;
				return acc;
			}, {});
			const currentMap = get().valueLabelMaps.goalOutlook;

			get().setValueLabelMap("goalOutlook", { ...currentMap, ...goalOutlooks });

			return formattedData;
		},
		/**
		 * Pure API calls:
		 */
		fetchSelectFieldOptions: async (customFieldName: string) => {
			const client = getClient();

			const { data } = await client.query({
				query: GET_SELECT_FIELD_OPTIONS,
				variables: { name: customFieldName },
			});
			const {
				fields: [{ options }],
			} = data;
			const fields = options.map((value) => ({ value, label: value }));
			return fields;
		},
	};
});

const useStore = createHook<QueryBuilderStore>(store);
export default useStore;
